import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { IUser, MobileAppSubscriptionStatus, UserRole } from '~/types/user-info-types'
import { getMe, getStreak, updateUser } from '~/redux/apis/user-api'
import { logout } from '~/auth/slices/auth.slice'

import { RootState } from '~/redux/store'
import { selectMappingQuizStatus } from '~/gamification/slices/gamification.slice'
import {
  isAccountTypeStepComplete,
  isMappingQuizStepComplete,
  isUserDetailsComplete,
} from '~/onboarding/utils/step-completion-check.util'
import { StripeSubscriptionStatus } from '~/types/stripe-subscription'
import { getCurrentGrade } from '~/curriculum/apis/curriculum.api'

import { getSubScriptionFromUser } from '~/familyAccount/utils/common.utils'

import { isSubscriptionRequiredForUser } from '../utils/subscription'
import { SuccessResult } from '../apis/types'

interface IUserWithCurrentGrade extends IUser {
  currentGrade?: number
}
const initialState: {
  current: null | IUserWithCurrentGrade
  cookieConsentApplicable: boolean
  showCookieConsentBanner: boolean
  streakDays: number | null
} = {
  current: null,
  cookieConsentApplicable: false,
  showCookieConsentBanner: false,
  streakDays: null,
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCookieConsentApplicable: (state, action: PayloadAction<boolean>) => ({
      ...state,
      cookieConsentApplicable: action.payload,
    }),
    setShowCookieConsentBanner: (state, action: PayloadAction<boolean>) => ({
      ...state,
      showCookieConsentBanner: action.payload,
    }),
    setCurrentUser: (state, action: PayloadAction<IUser>) => {
      state.current = action.payload
    },
    setStreakDays: (state, action: PayloadAction<number>) => {
      state.streakDays = action.payload
    },
    setShouldShowReferralModal: (state, action: PayloadAction<boolean>) => {
      if (state.current) {
        state.current.shouldShowReferralModal = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, (state) => {
        state.current = null
      })
      .addMatcher(getMe.matchFulfilled, (state, action: PayloadAction<IUser>) => {
        const loggedInAt = state.current?.loggedInAt
        const subscriptionData = getSubScriptionFromUser(action.payload)
        state.current = {
          ...action.payload,
          ...subscriptionData,
          curriculumCountryCode: action.payload.curriculumCountryCode?.toLowerCase(),
          loggedInAt: loggedInAt ?? new Date().toISOString(),
        }
      })
      .addMatcher(updateUser.matchFulfilled, (state, action: PayloadAction<SuccessResult<IUser>>) => {
        const loggedInAt = state.current?.loggedInAt
        const subscriptionData = getSubScriptionFromUser(action.payload.data)

        state.current = {
          ...action.payload.data,
          ...subscriptionData,
          curriculumCountryCode: (action.payload.data as IUser).curriculumCountryCode?.toLowerCase(),
          loggedInAt: loggedInAt ?? new Date().toISOString(),
        }
      })
      .addMatcher(getCurrentGrade.matchFulfilled, (state, action: PayloadAction<{ grade: number | null }>) => {
        if (state.current) {
          state.current.currentGrade = action.payload.grade ?? undefined
        }
      })
      .addMatcher(getStreak.matchFulfilled, (state, action: PayloadAction<{ streakDays: number }>) => {
        state.streakDays = action.payload.streakDays ?? 1
      })
  },
})

const currentUser = (state: RootState): IUserWithCurrentGrade | null => state.user.current

export const selectCurrentUser = (state: RootState): IUserWithCurrentGrade | null => state.user.current ?? null

export const isSubscriptionRequiredOnUser = (urlPath?: string, considerPermissions?: boolean) =>
  createSelector(currentUser, (user) => {
    return isSubscriptionRequiredForUser(user, urlPath, considerPermissions)
  })

export const selectUserIsStudent = createSelector(
  currentUser,
  (user) => user && (user.role === UserRole.USER || user.role === UserRole.FREE_USER),
)

export const userHasAllRequiredFields = createSelector([selectCurrentUser], (user) => {
  return user && (user.facebookEmail || user.email || user.authEmail) && user.country && user.birthYear && user.nickname
})
export const selectCurrentUserEmail = (state: RootState): string =>
  state.user.current?.authEmail || state.user.current?.facebookEmail || state.user.current?.email || ''

export const selectCurrentUserRole = (state: RootState): UserRole | undefined => state.user.current?.role
export const selectUserOnboardingStepStatus = createSelector(
  [selectCurrentUser, selectMappingQuizStatus],
  (user, quizStatus) => {
    const isUserLoaded = user != null
    const isQuizStatusLoaded = !quizStatus.isLoading
    return {
      isLoading: !isUserLoaded || !isQuizStatusLoaded,
      accountType: isUserLoaded && isAccountTypeStepComplete(user),
      isVerified: isUserLoaded && user.emailVerified,
      isEmailAvailable: isUserLoaded && !user.email,
      isDetailsComplete: isUserLoaded && isUserDetailsComplete(user),
      mappingQuiz: isQuizStatusLoaded && isMappingQuizStepComplete(quizStatus),
    }
  },
)

export const selectUserDetailsComplete = createSelector(currentUser, (user) => {
  const userEmail = user?.facebookEmail || user?.email || user?.authEmail
  const parsedBirthYear = Number.parseInt(user?.birthYear || '')
  const isUserLoaded = user != null

  return {
    isLoading: !isUserLoaded,
    isComplete:
      !!userEmail && !!user?.nickname && !!parsedBirthYear && !!user?.country && (!!user?.skill || user?.skill === 0),
  }
})

export const selectUserCanAccessHomebaseViaNavLinks = createSelector([selectUserOnboardingStepStatus], (stepStatus) => {
  const { isLoading, mappingQuiz } = stepStatus

  return {
    isLoading,
    isAllowed: mappingQuiz,
  }
})

export const selectUserCountry = (state: RootState): string | null =>
  state.user.current?.userCountry ?? state.user.current?.country ?? null

export const selectSubscriptionStatus = (
  state: RootState,
): StripeSubscriptionStatus | MobileAppSubscriptionStatus | 'none' =>
  state.user.current?.stripeSubscription?.[0]?.subscriptionStatus ||
  state.user.current?.validMobileAppSubscription?.subscriptionStatus ||
  'none'

export const selectSubscriptionApplicable = (state: RootState): boolean =>
  state.user.current?.isSubscriptionApplicable || false

export const selectCookieConsentStatus = (state: RootState): boolean => state.user.cookieConsentApplicable

export const selectShowCookieConsentBanner = (state: RootState): boolean => state.user.showCookieConsentBanner

export const selectUserHasPermission = (state: RootState): boolean => state.user.current?.hasPermissions || false

export const selectCurrentGrade = (state: RootState): number | undefined => state.user.current?.currentGrade

export const selectStreakDays = (state: RootState): number | null => state.user.streakDays ?? 1

export default userSlice.reducer
export const {
  setCookieConsentApplicable,
  setShowCookieConsentBanner,
  setCurrentUser,
  setStreakDays,
  setShouldShowReferralModal,
} = userSlice.actions
